define('webshopAttributesProductInfoView',[
  'module',
  'app',
  'marionette',
  'underscore',
  'settings',
  'entities/webshop/attribute_matching',
  'template!webshopAttributesProductInfoView',
  'entities/webshop/products'
], function (
  Module,
  App,
  Marionette,
  _,
  Settings,
  MatchingApi,
  Tpl
) {
  'use strict';

  var TEXT_TYPES = ['short', 'long', 'extra-description'];

  // Webshop texts are HTML written in the webshop admin: shown as HTML, without scripts,
  // embedded frames or event handlers.
  var sanitize = function (html) {
    var doc = new DOMParser().parseFromString('<div>' + (html || '') + '</div>', 'text/html');
    _.each(doc.querySelectorAll('script, style, iframe, object, embed, link, meta, form'), function (el) { el.remove(); });
    _.each(doc.querySelectorAll('*'), function (el) {
      _.each(_.toArray(el.attributes), function (attr) {
        if (/^on/i.test(attr.name) || (/^(href|src)$/i.test(attr.name) && /^\s*javascript:/i.test(attr.value)))
          el.removeAttribute(attr.name);
      });
    });
    return doc.body.firstChild.innerHTML;
  };

  // Product of a webshop category: its webshop name, picture, code and status (disabled /
  // outdated), and a window with its webshop descriptions. Built from the webshop category products API.
  var Helper = {
    // p: raw product of the webshop API ({code, label, status, texts, medias})
    wrap: function (p) {
      var model = App.request('webshop.product.model', p);
      return {code: p.code, label: p.label, status: p.status, model: model, name: Helper.name(model, p.label)};
    },

    // Name in the dashboard language, then English / French, then the webshop label.
    name: function (model, label) {
      var lang = Settings.get('lang');
      return model.getName(lang) || model.getName('en') || model.getName('fr') || label || model.getCode();
    },

    cellHtml: function (product) {
      return '<img class="product-picture" src="' + _.escape(product.model.getPreviewImageUrl()) + '" loading="lazy" alt=""/>' +
        '<div class="product-text">' +
        '<div class="product-name" title="' + _.escape(product.name) + '">' + _.escape(product.name) + '</div>' +
        '<div class="product-code">' + _.escape(product.code) +
        (product.status > 0 ? '' : ' <span class="am-badge">' + _.escape(_.i18n('common.disabled', null, 'Disabled')) + '</span>') +
        (product.model.getOutdated() ? ' <span class="am-badge outdated" title="' + _.escape(_.i18n('webshop.product.outdated.title', null, 'Outdated')) + '">' +
          _.escape(_.i18n('attributeMatching.outdated', null, 'Outdated')) + '</span>' : '') +
        '</div></div>' +
        '<span class="product-info" title="' + _.escape(_.i18n('attributeMatching.webshopTexts', null, 'Webshop descriptions')) + '"><i class="fa fa-info-circle"></i></span>';
    },

    // "Hide outdated / disabled products" of the two category screens, remembered in the browser.
    hideOptions: function () {
      var options = {outdated: false, disabled: false};
      try {
        _.extend(options, JSON.parse(window.localStorage.getItem('attributeMatching.hideProducts') || '{}'));
      } catch (e) {
        // private mode / blocked storage: defaults
      }
      return options;
    },

    setHideOptions: function (options) {
      try {
        window.localStorage.setItem('attributeMatching.hideProducts', JSON.stringify(options));
      } catch (e) {
        // not remembered
      }
    },

    visible: function (products, options) {
      return _.filter(products, function (p) {
        var outdated = options.outdated && p.model.getOutdated();
        var disabled = options.disabled && p.status <= 0;
        return !outdated && !disabled;
      });
    },

    // Search of the two category screens: code, webshop name or label contains the text.
    matches: function (product, query) {
      var q = (query || '').trim().toLowerCase();
      return !q || (product.code + ' ' + product.name + ' ' + (product.label || '')).toLowerCase().indexOf(q) !== -1;
    },

    // Legend of the value chips of the two category screens.
    legendHtml: function () {
      var sample = function (cls, text) { return '<span class="am-chip ' + cls + '">' + _.escape(text) + '</span>'; };
      return '<div class="am-legend">' +
        '<span class="legend-item">' + sample('', _.i18n('attributeMatching.legendSupplierChip', null, 'Cotton')) + ' ' +
          _.escape(_.i18n('attributeMatching.legendSupplier', null, 'supplier value (PIM import): locked here, change its matching to remove it')) + '</span>' +
        '<span class="legend-item">' + sample('manual', _.i18n('attributeMatching.legendManualChip', null, 'Bamboo')) + ' ' +
          _.escape(_.i18n('attributeMatching.legendManual', null, 'manual value: added here, can be removed here')) + '</span>' +
        '<span class="legend-item">' + sample('no-filter', _.i18n('attributeMatching.legendNoFilterChip', null, 'Linen')) + ' ' +
          _.escape(_.i18n('attributeMatching.legendNoFilter', null, 'struck through: shown in the product attributes but NOT a webshop filter — ' +
            'the supplier value has a "Shown as" matching and no "Filter" matching; ' +
            'set Filter 1 in Attributes & matching → Supplier values matching, then republish')) + '</span>' +
        '</div>';
    },

    // "Include sub-categories" of the category screens (class with-subcategories, URL state "sub").
    subHtml: function (on) {
      return '<label class="hide-option" title="' + _.escape(_.i18n('attributeMatching.withSubcategoriesTitle', null,
        'Also the products of every sub-category (whole sub-tree)')) + '"><input type="checkbox" class="with-subcategories" ' + (on ? 'checked' : '') + '/> ' +
        _.escape(_.i18n('attributeMatching.withSubcategories', null, 'Include sub-categories')) + '</label>';
    },

    // Parameters of the webshop category products API, with the sub-categories if asked.
    productParams: function (withSubcategories, params) {
      params = _.extend({}, params);
      if (withSubcategories)
        params.withSubcategories = 1;
      return params;
    },

    // Checkboxes for the toolbars (classes hide-outdated / hide-disabled).
    hideHtml: function (options) {
      return '<label class="hide-option"><input type="checkbox" class="hide-outdated" ' + (options.outdated ? 'checked' : '') + '/> ' +
        _.escape(_.i18n('attributeMatching.hideOutdated', null, 'Hide outdated')) + '</label>' +
        '<label class="hide-option"><input type="checkbox" class="hide-disabled" ' + (options.disabled ? 'checked' : '') + '/> ' +
        _.escape(_.i18n('attributeMatching.hideDisabled', null, 'Hide disabled')) + '</label>';
    },

    open: function (product) {
      require(['windows', 'webshopAttributesProductInfoView'], function (Windows, View) {
        Windows.open({
          view: function () {
            return new View({product: product});
          },
          modal: true,
          width: 760,
          height: 620,
          minWidth: 420,
          minHeight: 300,
          title: product.name + ' (' + product.code + ')'
        });
      });
    }
  };

  var View = Marionette.ItemView.extend({
    template: Tpl,
    className: 'webshop-attributes-product-info-view',

    events: {
      'click .lang': 'onLang'
    },

    initialize: function () {
      var texts = this.options.product.model.get('texts') || [];
      this.languages = _.uniq(_.compact(_.pluck(texts, 'lang')));
      var current = Settings.get('lang');
      this.lang = _.contains(this.languages, current) ? current : (this.languages[0] || current);
      // PIM options / attributes of the product, per language (loaded when shown)
      this.optionsByLang = {};
      this.attributesByLang = {};
    },

    onRender: function () {
      this.renderOptions();
      this.renderAttributes();
    },

    renderAttributes: function () {
      var lang = this.lang;
      var $region = this.$('.pim-attributes');
      var data = this.attributesByLang[lang];
      if (!data) {
        $region.addClass('am-loading').html('');
        MatchingApi.productAttributes(this.options.product.code, lang).done(_.bind(function (result) {
          this.attributesByLang[lang] = result;
          if (!this.isDestroyed && this.lang === lang)
            this.renderAttributes();
        }, this)).fail(_.bind(function (message) {
          if (!this.isDestroyed)
            this.$('.pim-attributes').removeClass('am-loading').html('<div class="empty">' + _.escape(message) + '</div>');
        }, this));
        return;
      }
      $region.removeClass('am-loading');
      if (!data.found || !data.fields.length) {
        $region.html('<div class="empty">' + (data.found ?
          _.i18n('attributeMatching.noPimAttribute', null, 'No attribute') :
          _.i18n('attributeMatching.noPimProduct', null, 'No PIM sheet: values cannot be assigned')) + '</div>');
        return;
      }
      var notMatched = _.i18n('attributeMatching.notMatched', null, 'not matched');
      $region.html('<div class="attr-row head"><div class="attr-field">' + _.escape(_.i18n('attributeMatching.pimAttribute', null, 'PIM attribute')) + '</div>' +
        '<div class="attr-values">' + _.escape(_.i18n('attributeMatching.pimValueMatching', null, 'Value → shown as / filters')) + '</div></div>' +
        _.map(data.fields, function (f) {
          return '<div class="attr-row">' +
            '<div class="attr-field"><div class="field-name">' + _.escape(f.name) +
            (f.source === 'MANUAL' ? ' <span class="am-badge manual">' + _.escape(_.i18n('attributeMatching.manualShort', null, 'manual')) + '</span>' : '') + '</div>' +
            '<div class="field-key ' + (f.keyId ? '' : 'none') + '">' + (f.keyId ?
              '→ ' + _.escape(f.keyName) + (f.keyIsFilter ? '' : ' <span class="am-hint">(' + _.escape(_.i18n('attributeMatching.notFilterShort', null, 'not a filter')) + ')</span>') :
              _.escape(notMatched)) + '</div>' +
            '<div class="field-code">' + _.escape(f.code) + '</div></div>' +
            '<div class="attr-values">' + _.map(f.values, function (v) {
              var matched = v.shownAs || v.filters.length;
              return '<div class="attr-value"><span class="value-label">' +
                (v.color ? '<span class="am-swatch" style="background:' + _.escape(v.color) + '"></span> ' : '') + _.escape(v.label) + '</span>' +
                '<span class="value-match ' + (matched ? '' : 'none') + '">' + (matched ?
                  '→ ' + (v.shownAs ? '<b>' + _.escape(v.shownAs) + '</b>' : '—') +
                  (v.filters.length ? ' / ' + _.map(v.filters, function (n) { return '<span class="filter-name">' + _.escape(n) + '</span>'; }).join(', ') : '') :
                  _.escape(notMatched)) + '</span></div>';
            }).join('') + '</div></div>';
        }).join(''));
    },

    renderOptions: function () {
      var lang = this.lang;
      var $region = this.$('.options');
      var options = this.optionsByLang[lang];
      if (!options) {
        $region.addClass('am-loading').html('');
        MatchingApi.productOptions(this.options.product.code, lang).done(_.bind(function (result) {
          this.optionsByLang[lang] = result;
          if (!this.isDestroyed && this.lang === lang)
            this.renderOptions();
        }, this)).fail(_.bind(function (message) {
          if (!this.isDestroyed)
            this.$('.options').removeClass('am-loading').html('<div class="empty">' + _.escape(message) + '</div>');
        }, this));
        return;
      }
      $region.removeClass('am-loading');
      if (!options.found || !options.categories.length) {
        $region.html('<div class="empty">' + (options.found ?
          _.i18n('attributeMatching.noOption', null, 'No option') :
          _.i18n('attributeMatching.noPimProduct', null, 'No PIM sheet: values cannot be assigned')) + '</div>');
        return;
      }
      $region.html(_.map(options.categories, function (c) {
        return '<div class="option-category"><div class="category-name">' + _.escape(c.name) +
          ' <span class="count">(' + c.options.length + ')</span></div><div class="option-list">' +
          _.map(c.options, function (o) {
            return '<span class="option ' + (o.active ? '' : 'inactive') + '"' +
              (o.active ? '' : ' title="' + _.escape(_.i18n('attributeMatching.optionInactive', null, 'Deactivated for your webshop')) + '"') + '>' +
              (o.color ? '<span class="am-swatch" style="background:' + _.escape(o.color) + '"></span>' : '') +
              _.escape(o.name) + '</span>';
          }).join('') + '</div></div>';
      }).join(''));
    },

    serializeData: function () {
      var model = this.options.product.model;
      var lang = this.lang;
      return {
        image: model.getPreviewImageUrl(),
        images: model.getImageUrls ? model.getImageUrls() : [],
        name: model.getName(lang) || this.options.product.name,
        code: this.options.product.code,
        languages: this.languages,
        lang: lang,
        sections: _.compact(_.map(TEXT_TYPES, function (type) {
          var html = model.getText(type, lang);
          if (!html)
            return null;
          return {
            title: {
              'short': _.i18n('attributeMatching.textShort', null, 'Short description'),
              'long': _.i18n('attributeMatching.textLong', null, 'Description'),
              'extra-description': _.i18n('attributeMatching.textExtra', null, 'Extra description')
            }[type],
            html: sanitize(html)
          };
        }))
      };
    },

    onLang: function (e) {
      this.lang = $(e.currentTarget).data('lang');
      this.render();
    }
  });

  View.Helper = Helper;
  Module.exports = View;
});

